import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { portalFetch, setAntiforgeryToken } from "./api/http";
import { LoginMfaPage, mfaTextKeys } from "./auth/LoginMfaPage";
import { LoginPage, loginTextKeys } from "./auth/LoginPage";
import { localeFromCookie, missingText } from "./i18n/texts";
import { ChatPage } from "./pages/chat/ChatPage";
import { HomePage } from "./pages/HomePage";
import { PreferencesPage } from "./pages/account/PreferencesPage";
import { ProfilePage } from "./pages/account/ProfilePage";
import { SecurityPage } from "./pages/account/SecurityPage";
import { CompanyPage } from "./pages/settings/CompanyPage";
import { SimulatorPage } from "./pages/SimulatorPage";
import { ContactsPage } from "./pages/chat/ContactsPage";
import { ParticipantsPage } from "./pages/chat/ParticipantsPage";
import { RepliesPage } from "./pages/chat/RepliesPage";
import { TagsPage } from "./pages/chat/TagsPage";
import {
  ChannelRulesPage,
  ChannelsPage,
  ChatSettingsPage,
  InboxDetailPage,
  InboxesPage,
  InboxNewPage,
  InboxRoutingPage,
  RolesPage,
  SettingsPage,
  StatisticsPage,
  UserNewPage,
  UsersPage,
  WhatsappPage,
} from "./pages/config/lazyPages";
import { NumericRoute } from "./pages/config/NumericRoute";
import { ChangePasswordPage, changePasswordTextKeys } from "./pages/cuenta/ChangePasswordPage";
import { InactiveOrganizationPage, inactiveOrganizationTextKeys } from "./pages/cuenta/InactiveOrganizationPage";
import { InvitationPage, invitationTextKeys } from "./pages/cuenta/InvitationPage";
import { OrganizationsPage } from "./pages/cuenta/OrganizationsPage";
import { RecoverPage, recoverTextKeys } from "./pages/cuenta/RecoverPage";
import { RegisterPage, registerTextKeys } from "./pages/cuenta/RegisterPage";
import { SupportDeniedPage, SupportEndedPage, supportTextKeys } from "./pages/cuenta/SupportPages";
import { AvailabilityPage, TeamPage, TeamRedirect } from "./pages/chat/TeamPage";
import { ReportViews } from "./reports/ReportViews";
import { Shell } from "./shell/Shell";
import { confirmLeave, hasLeaveGuards } from "./shell/leaveGuard";
import { isMigrated, setLegacyBase } from "./shell/routes";
import { lazyScreen, preloadScreensFor, preloadScreensWhenIdle } from "./ui/lazyScreen";
import { shellTextKeys, type ShellData } from "./shell/shellModel";

// El chat (lista, conversación, Dock y tiempo real) va en su propio archivo: no pesa en la primera carga del portal.
// Al arrancar se pide solo en el chat de pantalla completa (isChatRoute): en las demás páginas el Dock llega en reposo,
// para que su arranque (lista y conversaciones) no compita con los datos de la pantalla que se abre.
const ChatDock = lazyScreen(
  () => import("./chat/components/dock").then((module) => module.ChatDock),
  /^\/portal\/chat(\/index)?$/,
);

// El simulador (ventana del shell) llega aparte: solo se trae cuando la persona lo abre o la pestaña lo tenía abierto.
const SimulatorPanel = lazyScreen(() => import("./simulator/SimulatorPanel").then((module) => module.SimulatorPanel));

// Las páginas legales públicas (B3) llegan aparte: se piden ya solo al abrir /legal; no pesan en la entrada del portal.
// Una carga de /legal/... (enlace del pie, Meta) la responde el servidor con HTML sin JavaScript (Session/LegalPageEndpoints.cs,
// mismos textos); esta ruta solo dibuja la página si la SPA ya abierta navega a ella.
const LegalPage = lazyScreen(() => import("./legal/LegalPage").then((module) => module.LegalPage), /^\/legal(\/|$)/);

// El código de la pantalla que se abre se pide ya, en paralelo con la sesión y el shell: llega antes que el marco y se
// dibuja sin la espera de Suspense (ui/lazyScreen). El de las demás pantallas, con el portal en reposo.
preloadScreensFor(window.location.pathname);

const queries = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false } },
});

type SessionState = { authenticated: boolean; antiforgeryToken: string; organizationId: number | null };

/** Selector de empresa: la única pantalla del portal que se abre con sesión y sin empresa elegida. */
const isOrganizationChooser = (path: string) => path.replace(/\/+$/, "").toLowerCase() === "/portal/organizaciones";

/** Sesión, shell y textos del shell: lo mínimo para pintar el marco de cualquier pantalla del portal. */
function useShell(locale: string, path: string) {
  const session = useQuery({ queryKey: ["session"], queryFn: () => portalFetch<SessionState>("/api/portal/session") });
  useEffect(() => {
    if (session.data) setAntiforgeryToken(session.data.antiforgeryToken);
  }, [session.data]);
  const signedIn = !!session.data?.authenticated && !!session.data.organizationId;
  // El selector de empresa (T18) se abre con sesión aún sin empresa elegida: el shell va vacío, como PortalLayout.
  const choosing = !!session.data?.authenticated && isOrganizationChooser(path);
  const shell = useQuery({
    queryKey: ["shell"],
    enabled: signedIn || choosing,
    queryFn: () => portalFetch<ShellData & { legacyPortalUrl: string }>("/api/portal/shell"),
  });
  const texts = useQuery({
    queryKey: ["texts", locale, "shell"],
    queryFn: () =>
      portalFetch<Record<string, string>>(
        `/api/portal/texts/${locale}?prefixes=${encodeURIComponent(shellTextKeys.join(","))}`,
      ),
  });
  useEffect(() => {
    if (shell.data) setLegacyBase(shell.data.legacyPortalUrl);
  }, [shell.data]);
  return { session, shell, texts, signedIn, choosing };
}

/**
 * Como FocusOnNavigate (Selector="h1") del portal actual: al cambiar de página intenta enfocar el título. El h1 no es
 * enfocable, así que el control activo pierde el foco y este queda en la página; la primera carga no cambia nada. Si
 * solo cambia la consulta (filtros, orden, página dentro de la misma vista), el foco se queda donde está: moverlo
 * rompería escribir en el buscador (Etiquetas R1: cada tecla aplicada desenfocaba el campo).
 */
function useFocusOnNavigate(key: string, pathname: string) {
  const first = useRef(true);
  const previous = useRef(pathname);
  useEffect(() => {
    const samePage = previous.current === pathname;
    previous.current = pathname;
    if (first.current) {
      first.current = false;
      return;
    }
    if (samePage) return;
    const heading = document.querySelector<HTMLElement>("h1");
    heading?.focus();
    if (document.activeElement !== heading) (document.activeElement as HTMLElement | null)?.blur();
  }, [key, pathname]);
}

/**
 * Como la navegación mejorada de blazor.web.js: un clic en un enlace a otra pantalla migrada cambia de página sin
 * recargar, así el shell y el Dock siguen vivos. Los enlaces al portal actual, a otra pestaña, con tecla modificadora,
 * de descarga o solo de ancla (#main) siguen siendo enlaces normales. Corre después de los Link del enrutador y de las
 * guardas de borrador que escuchan en captura (las dos marcan defaultPrevented) porque escucha en el documento.
 */
function useEnhancedNavigation() {
  const navigate = useNavigate();
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element | null)?.closest?.("a[href]");
      if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute("download")) return;
      if (anchor.target && anchor.target !== "_self") return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || !isMigrated(url.pathname)) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      event.preventDefault();
      const go = () => {
        navigate(url.pathname + url.search + url.hash);
        if (!url.hash) window.scrollTo(0, 0);
      };
      // Un editor con cambios sin guardar decide antes, como OnBeforeInternalNavigation en el portal actual.
      if (hasLeaveGuards()) void confirmLeave().then((leave) => leave && go());
      else go();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [navigate]);
}

/**
 * Cuándo montar el Dock: ya en el chat de pantalla completa, donde es la página; en las demás, un momento después de la
 * primera pintura y con el navegador libre, para que su arranque (lista, conversaciones y conexión) no compita con los
 * datos de la pantalla que se abre (en el portal actual el Dock llega prerenderizado y arranca después).
 */
function useDockStart(): boolean {
  const [start, setStart] = useState(() => /^\/portal\/chat(\/index)?\/?$/i.test(window.location.pathname));
  useEffect(() => {
    if (start) return;
    let idle: number | null = null;
    const timer = window.setTimeout(() => {
      if (typeof window.requestIdleCallback === "function")
        idle = window.requestIdleCallback(() => setStart(true), { timeout: 1000 });
      else setStart(true);
    }, 300);
    return () => {
      window.clearTimeout(timer);
      if (idle !== null) window.cancelIdleCallback(idle);
    };
  }, [start]);
  return start;
}

function PortalPage() {
  const location = useLocation();
  useEnhancedNavigation();
  const dockStart = useDockStart();
  // Con el portal ya pintado, el código de las demás pantallas aparte se trae en reposo (ui/lazyScreen).
  useEffect(() => preloadScreensWhenIdle(), []);
  const [locale] = useState(() => localeFromCookie());
  const { session, shell, texts, signedIn, choosing } = useShell(locale, location.pathname);
  const L = useMemo(() => (key: string) => texts.data?.[key] ?? missingText(key), [texts.data]);
  useEffect(() => {
    // Sin sesión o sin empresa elegida, el portal actual decide a dónde ir (login o selector) mientras dure la migración.
    if (session.data && !signedIn && !choosing)
      window.location.assign(`/login?ReturnUrl=${encodeURIComponent(location.pathname + location.search)}`);
  }, [session.data, signedIn, choosing, location.pathname, location.search]);
  // IDN-7: con la empresa inactiva no hay pantalla que abrir; el selector de empresa sí sigue disponible.
  const inactive = !!shell.data?.organizationInactive && !choosing;
  useEffect(() => {
    if (inactive) window.location.replace("/empresa/inactiva");
  }, [inactive]);
  useFocusOnNavigate(location.key, location.pathname);
  // Vistas de tabla: todas las claves de la ventana (R3_VIEW_*) y de sus avisos (C1_*), como CompanyReportViews.
  const reportTexts = useQuery({
    queryKey: ["texts", locale, "reports"],
    enabled: signedIn,
    queryFn: () => portalFetch<Record<string, string>>(`/api/portal/texts/${locale}?prefixes=R3_VIEW_,C1_`),
  });
  if (!shell.data || !texts.data || inactive) return null;
  const owner = shell.data.isSupport ? null : shell.data.reportScope;
  return (
    <Shell
      data={shell.data}
      L={L}
      locale={locale}
      path={location.pathname}
      search={location.search}
      simulationRoot={
        shell.data.employeesEnabled && dockStart ? (
          <Suspense fallback={null}>
            <SimulatorPanel owner={shell.data.reportScope} locale={locale} />
          </Suspense>
        ) : null
      }
      chatRoot={
        shell.data.chatEnabled && dockStart ? (
          <Suspense fallback={null}>
            <ChatDock />
          </Suspense>
        ) : null
      }
      reportViews={
        reportTexts.data ? (
          <ReportViews owner={owner} labels={reportTexts.data} path={location.pathname + location.search} />
        ) : null
      }
    >
      <Routes>
        <Route path="/portal" element={<HomePage title={L("NAV_HOME")} />} />
        <Route path="/portal/index" element={<HomePage title={L("NAV_HOME")} />} />
        <Route path="/portal/chat" element={<ChatPage locale={locale} search={location.search} />} />
        <Route path="/portal/chat/index" element={<ChatPage locale={locale} search={location.search} />} />
        <Route path="/portal/chat/configuracion/etiquetas" element={<TagsPage shell={shell.data} locale={locale} />} />
        <Route path="/portal/chats/:id/participantes" element={<Participants locale={locale} />} />
        {/* T29–T32 · Configuración del Chat, Roles y permisos (Etapa C), Bandejas, Canales y Estadísticas. */}
        <Route path="/portal/chat/configuracion" element={<ChatSettingsPage locale={locale} />} />
        <Route path="/portal/configuracion/chat" element={<ChatSettingsPage locale={locale} />} />
        <Route path="/portal/configuracion" element={<SettingsPage locale={locale} />} />
        <Route path="/portal/configuracion/index" element={<SettingsPage locale={locale} />} />
        <Route path="/portal/configuracion/usuarios" element={<UsersPage locale={locale} />} />
        <Route path="/portal/configuracion/empresa" element={<CompanyPage locale={locale} />} />
        <Route path="/portal/perfil" element={<ProfilePage locale={locale} />} />
        <Route path="/portal/seguridad" element={<SecurityPage locale={locale} />} />
        <Route path="/portal/preferencias" element={<PreferencesPage locale={locale} />} />
        <Route path="/portal/configuracion/usuarios/nuevo" element={<UserNewPage locale={locale} />} />
        <Route path="/portal/chat/configuracion/roles" element={<RolesPage locale={locale} />} />
        <Route
          path="/portal/chat/configuracion/roles/:roleId"
          element={<NumericRoute param="roleId" element={<RolesPage locale={locale} />} />}
        />
        <Route
          path="/portal/chat/configuracion/bandejas/:inboxId/reparto"
          element={<NumericRoute param="inboxId" element={<InboxRoutingPage locale={locale} />} />}
        />
        <Route path="/portal/bandejas" element={<InboxesPage locale={locale} />} />
        <Route path="/portal/bandejas/index" element={<InboxesPage locale={locale} />} />
        <Route path="/portal/bandejas/nueva" element={<InboxNewPage locale={locale} />} />
        <Route path="/portal/bandejas/detalle" element={<InboxDetailPage key="detalle" locale={locale} />} />
        <Route path="/portal/bandejas/:id" element={<NumericRoute element={<InboxDetailPage locale={locale} />} />} />
        <Route path="/portal/canales" element={<ChannelsPage locale={locale} />} />
        <Route path="/portal/canales/index" element={<ChannelsPage locale={locale} />} />
        <Route path="/portal/chat/estadisticas" element={<StatisticsPage locale={locale} />} />
        <Route path="/portal/canales/whatsapp" element={<WhatsappPage locale={locale} />} />
        <Route path="/portal/canales/reglas" element={<ChannelRulesPage key="reglas" locale={locale} />} />
        <Route
          path="/portal/canales/:id/reglas"
          element={<NumericRoute element={<ChannelRulesPage locale={locale} />} />}
        />
        <Route path="/portal/chat/contactos/*" element={<ContactsPage shell={shell.data} locale={locale} />} />
        <Route path="/portal/chat/respuestas" element={<RepliesPage locale={locale} />} />
        <Route path="/portal/organizaciones" element={<OrganizationsPage locale={locale} />} />
        <Route path="/portal/simulacion" element={<SimulatorPage L={L} search={location.search} />} />
        {/* Chat › Equipo (T33–T34): Miembros, Configurar agente, acceso efectivo y Grupos. */}
        {[
          "/portal/chat/equipo",
          "/portal/chat/equipo/miembros",
          "/portal/chat/equipo/miembros/:memberId",
          "/portal/chat/equipo/miembros/:memberId/acceso",
          "/portal/chat/equipo/grupos",
          "/portal/chat/equipo/grupos/:groupId",
        ].map((path) => (
          <Route key={path} path={path} element={<TeamPage locale={locale} />} />
        ))}
        {/* Horarios y disponibilidad de Chat › Configuración (Horarios y Feriados y cierres). */}
        {[
          "/portal/chat/configuracion/horarios",
          "/portal/chat/configuracion/horarios/:scheduleId",
          "/portal/chat/configuracion/cierres",
        ].map((path) => (
          <Route key={path} path={path} element={<AvailabilityPage locale={locale} />} />
        ))}
        {/* Direcciones viejas de Equipo (EquipoAnterior.razor). */}
        {[
          "/portal/chat/equipo/horarios",
          "/portal/chat/equipo/horarios/:scheduleId",
          "/portal/chat/equipo/cierres",
          "/portal/chat/equipo/roles",
          "/portal/chat/equipo/roles/:roleId",
          "/Portal/Configuracion/Horarios",
        ].map((path) => (
          <Route key={path} path={path} element={<TeamRedirect />} />
        ))}
        <Route path="*" element={<div data-testid="portal-root" />} />
      </Routes>
    </Shell>
  );
}

/** Participantes de un grupo interno: el id de la ruta (solo números, como {Id:long}). */
function Participants({ locale }: { locale: string }) {
  const { id } = useParams();
  return /^\d+$/.test(id ?? "") ? (
    <ParticipantsPage key={id} locale={locale} id={Number(id)} />
  ) : (
    <div data-testid="portal-root" />
  );
}

/**
 * Flujos de ingreso (sin shell ni sesión): cada pantalla pide solo sus textos. Van fuera de PortalPage para que la
 * falta de sesión no vuelva a redirigir a /login.
 */
function AuthPage({ keys, page }: { keys: string[]; page: (L: (key: string) => string) => React.ReactNode }) {
  const [locale] = useState(() => localeFromCookie());
  const texts = useQuery({
    queryKey: ["texts", locale, keys.join(",")],
    queryFn: () =>
      portalFetch<Record<string, string>>(`/api/portal/texts/${locale}?prefixes=${encodeURIComponent(keys.join(","))}`),
  });
  const L = useMemo(() => (key: string) => texts.data?.[key] ?? missingText(key), [texts.data]);
  return texts.data ? <>{page(L)}</> : null;
}

export function App() {
  return (
    <QueryClientProvider client={queries}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<AuthPage keys={loginTextKeys} page={(L) => <LoginPage L={L} />} />} />
          <Route path="/loginmfa" element={<AuthPage keys={mfaTextKeys} page={(L) => <LoginMfaPage L={L} />} />} />
          <Route path="/recuperar" element={<AuthPage keys={recoverTextKeys} page={(L) => <RecoverPage L={L} />} />} />
          <Route path="/register" element={<AuthPage keys={registerTextKeys} page={(L) => <RegisterPage L={L} />} />} />
          <Route
            path="/cambiarpassword"
            element={<AuthPage keys={changePasswordTextKeys} page={(L) => <ChangePasswordPage L={L} />} />}
          />
          <Route
            path="/invitacion/aceptar"
            element={<AuthPage keys={invitationTextKeys} page={(L) => <InvitationPage L={L} />} />}
          />
          <Route
            path="/soporte/denegado"
            element={<AuthPage keys={supportTextKeys} page={(L) => <SupportDeniedPage L={L} />} />}
          />
          <Route
            path="/soporte/fin"
            element={<AuthPage keys={supportTextKeys} page={(L) => <SupportEndedPage L={L} />} />}
          />
          {/* IDN-7: empresa inactiva, fuera del shell (ningún módulo está permitido). */}
          <Route
            path="/empresa/inactiva"
            element={<AuthPage keys={inactiveOrganizationTextKeys} page={(L) => <InactiveOrganizationPage L={L} />} />}
          />
          {/* B3 · Páginas legales públicas (privacidad, eliminación de datos y términos): sin sesión ni shell. */}
          <Route
            path="/legal"
            element={
              <Suspense fallback={null}>
                <LegalPage />
              </Suspense>
            }
          />
          <Route
            path="/legal/:slug"
            element={
              <Suspense fallback={null}>
                <LegalPage />
              </Suspense>
            }
          />
          <Route path="*" element={<PortalPage />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
